import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { dispatchAnnotationEvent } from '@components/annotations/annotationEvent';
import useFetchAnnotations from '@components/annotations/hooks/useFetchAnnotations';
import { annotationsFeatureSelector } from '@store/common/selectors';
import { findAxisIndex } from '@components/metrics-panel-hz/hooks/utils';
import { TIME_AXIS, VALUE_AXIS } from '@components/metrics-panel-hz/utils/lineChartInputSchema';
import { getAnnotationOption } from './annotationOption';

// modifies input series
export function formatAnnotationSeries(option, annotations = []) {
  // add annotation series
  const annotationSeries = getAnnotationOption(annotations, {
    xAxisIndex: findAxisIndex(option.xAxis, TIME_AXIS),
    yAxisIndex: findAxisIndex(option.yAxis, VALUE_AXIS),
  });

  return { series: [annotationSeries] };
}

// additionalTriggerDeps:  workaround to trigger annotations layer on complex flows like diagnostics
// where overlapping paints rewrite the option set
function useAnnotations({
  metric,
  instance,
  enabled = false,
  intervalSelector,
  additionalTriggerDeps = [],
}) {
  const isEnabled = useSelector(annotationsFeatureSelector) && enabled;
  const interval = useSelector(intervalSelector);

  const { data: annotations = [] } = useFetchAnnotations({ interval, metric });

  useEffect(() => {
    if (instance && isEnabled) {
      // workaround to avoid race conditions in updates
      // a better approach is to accept option and modify it
      // let a single effect do setOption
      // diagnostics does not have a right place to do this, deferring till we can tweak that code
      setTimeout(() => {
        const option = instance.getOption();
        if (option) {
          const { series } = formatAnnotationSeries(option, annotations);
          instance.setOption({ series });
        }
      }, 100);
    }
  }, [instance, metric, isEnabled, JSON.stringify(annotations), ...additionalTriggerDeps]);

  const handleAnnotationClick = useCallback(
    (params) => {
      if (params.componentType === 'markLine' && 'annotation' in params.data) {
        dispatchAnnotationEvent({
          metricKey: metric,
          annotationId: params.data.annotation.annotationId,
        });
      }
    },
    [metric],
  );

  useEffect(() => {
    if (instance && isEnabled) {
      instance.on('click', handleAnnotationClick);
      return () => {
        instance.off('click', handleAnnotationClick);
      };
    }
  }, [instance, metric, isEnabled]);
}

export default useAnnotations;
